import { DateSalesWithTz } from '@eventbrite/event-listing-service-client';
import styles from '../EventDetails.module.scss';
import { useIntl } from 'react-intl';
import { useMemo } from 'react';
import { getDisplayTimezone, getEventDateTimeDisplayString } from '@/utils/dateTime';
import { useAttendeeTimezone } from '@/hooks/useAttendeeTimezone';
import { MULTIPLE_DATES } from '@/components/_shared/constants';
import { CalendarIcon } from '@/components/_shared/Icons/CalendarIcon';

type EventDateTimeProps = {
  startDate: DateSalesWithTz;
  endDate: DateSalesWithTz;
  locale: string;
  isOnlineEvent: boolean;
  isRecurringEvent: boolean;
  hideStartDate?: boolean;
  hideEndDate?: boolean;
};

export const EventDateTime = ({
  startDate,
  endDate,
  locale,
  isOnlineEvent,
  isRecurringEvent,
  hideStartDate = false,
  hideEndDate = false,
}: EventDateTimeProps) => {
  const { formatMessage } = useIntl();
  const attendeeTimezone = useAttendeeTimezone(isOnlineEvent && !isRecurringEvent && !!startDate.utc);

  const displayText = useMemo(() => {
    if (!startDate.utc) {
      return null;
    }

    const timezone = getDisplayTimezone({
      isOnlineEvent,
      eventTimezone: startDate.timezone,
      attendeeTimezone,
    });

    return getEventDateTimeDisplayString({
      startDate,
      endDate,
      hideStartDate,
      hideEndDate,
      locale,
      timezone,
      formatMessage,
      isRecurringEvent,
      recurringEventLabel: isRecurringEvent ? formatMessage(MULTIPLE_DATES) : undefined,
      showTimezone: isOnlineEvent,
    });
  }, [
    isRecurringEvent,
    startDate,
    endDate,
    isOnlineEvent,
    locale,
    formatMessage,
    hideStartDate,
    hideEndDate,
    attendeeTimezone,
  ]);

  if (!displayText) {
    return null;
  }

  const machineReadableDateTime = hideStartDate && !hideEndDate && endDate.utc ? endDate.utc : startDate.utc;

  return (
    <div className={styles.secondaryText} data-testid="event-datetime">
      <CalendarIcon className={styles.calendarIcon} />
      {isRecurringEvent ? <span>{displayText}</span> : <time dateTime={machineReadableDateTime}>{displayText}</time>}
    </div>
  );
};
